import classnames from 'classnames';
import { isToday } from 'date-fns/isToday';
import React, { useMemo } from 'react';

import { getZonedDate, ZonedDate } from '@eika-infrastruktur/date';
import { postMessageRN, runningInRNWebView, WebToRNAction } from '@eika-infrastruktur/mobilbank-lib';
import { LinkButton } from '@eika/button';
import { InfoCard } from '@eika/card';
import { CheckmarkAnimation, CheckmarkAnimationTypes } from '@eika/checkmark-animation';
import { Link, LinkType } from '@eika/link';
import { RichIcon, RichIconTypes } from '@eika/rich-icon';

import { useConfig } from '../../defaultConfig';
import type { ApprovalStatus, PaymentType } from '../../generated-types';
import useTranslation from '../../hooks/useTranslation';
import useTriggerEffect from '../../hooks/useTriggerEffect';
import { useUnactivatedPush } from '../../hooks/useUnactivatedPush';
import { pushTrackIDs } from '../../utils/gtm/trackIDs/push';

import './TransactionModal.scss';
import './TransactionResultInfo.scss';

export interface Props {
    type: PaymentType | 'downpayment' | 'efaktura-sharing';
    status?: ApprovalStatus;
    onClose: () => void;
    paymentDate?: ZonedDate;
    redirectLinkAvtalegiro?: string;
    successButtons?: boolean;
    isPlural?: boolean;
    isBeingSentForApproval?: boolean;
}

const TransactionSuccessResultInfo: React.FC<Props> = ({
    type,
    status,
    onClose,
    paymentDate = getZonedDate(),
    redirectLinkAvtalegiro,
    successButtons = false,
    isPlural = false,
    isBeingSentForApproval = false,
}) => {
    const {
        frontPageLink,
        pushSettingsLink,
        featureToggles: { disableAvtaleGiroChanges, enableAskForReview, nudgePushOnEfaktura },
    } = useConfig();
    const translated = useTranslation();

    const infoMessage = translated.paymentInfoMessages[type];

    const successText = useMemo(() => {
        if (isBeingSentForApproval) {
            return translated.paymentForApproval;
        }
        if (paymentDate && isToday(paymentDate)) {
            return isPlural ? infoMessage?.successPlural : infoMessage?.success;
        }
        return infoMessage?.successFuture ?? '';
    }, [paymentDate, translated, infoMessage, isPlural, isBeingSentForApproval]);

    useTriggerEffect(
        status === 'COMPLETED',
        () => {
            if (runningInRNWebView && enableAskForReview) {
                postMessageRN({
                    // send a signal each time, but native ignores it if user has been asked to rate before
                    action: WebToRNAction.SHOW_FEEDBACK_MODAL,
                    payload: 3000,
                });
            }
        },
        [enableAskForReview, frontPageLink?.url, status],
    );

    const couldHavePush = useUnactivatedPush(nudgePushOnEfaktura && type === 'EFAKTURA');

    if (couldHavePush) {
        return (
            <InfoCard skipBackground>
                <RichIcon type={RichIconTypes.MOBILBANK_APP_KUN_TELEFON} />
                <InfoCard.TitleH3>{translated.transactionSuccessPushNudge.title}</InfoCard.TitleH3>
                <p className="typography-ingress">{translated.transactionSuccessPushNudge.description}</p>

                <Link
                    linkType={LinkType.BUTTON_LARGE_POSITIVE}
                    href={pushSettingsLink!.url}
                    data-track-id={pushTrackIDs.nudge}
                    data-track-linkname={translated.transactionSuccessPushNudge.buttonText}
                >
                    {translated.transactionSuccessPushNudge.buttonText}
                </Link>
                <LinkButton
                    onClick={onClose}
                    data-track-id={pushTrackIDs.nudge}
                    data-track-linkname={translated.transactionSuccessPushNudge.abortButtonText}
                >
                    {translated.transactionSuccessPushNudge.abortButtonText}
                </LinkButton>
            </InfoCard>
        );
    }
    return (
        <div className="stretch-modal-content">
            <div
                className={classnames('transfer-success', {
                    'link-avtalegiro': redirectLinkAvtalegiro && !disableAvtaleGiroChanges,
                })}
            >
                <CheckmarkAnimation
                    className="transfer-success-checkmark--centered"
                    checkmarkAnimationType={CheckmarkAnimationTypes.POSITIVE}
                />

                <h3 className="typography-h3">{successText}</h3>
            </div>
            {successButtons && (
                <section className="success-result__submit-wrapper">
                    <LinkButton onClick={onClose}>{translated.close}</LinkButton>
                    <Link
                        linkType={LinkType.BUTTON_STANDARD_POSITIVE}
                        href={frontPageLink?.url ?? '/'}
                        data-testid="to-frontpage-button"
                    >
                        {translated.updatePaymentLimits.result.toFrontPage}
                    </Link>
                </section>
            )}
            {redirectLinkAvtalegiro && !disableAvtaleGiroChanges && (
                <div className="redirect-link-avtalegiro">
                    <h4 className="redirect-link-header">{translated.avtaleGiroAgreement.redirectLink.header}</h4>
                    <p className="redirect-link-description">
                        {translated.avtaleGiroAgreement.redirectLink.description}
                    </p>
                    <Link href={redirectLinkAvtalegiro}>{translated.avtaleGiroAgreement.redirectLink.link}</Link>
                </div>
            )}
        </div>
    );
};

export default TransactionSuccessResultInfo;
